import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Chip, Tooltip, IconButton, CircularProgress, Stack, Drawer,
  alpha,
} from '@mui/material';
import {
  Close, Search, Warning, Star, TrendingUp, Bolt,
  Psychology, Equalizer, ShowChart, Assessment, FileDownloadOutlined,
} from '@mui/icons-material';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip,
} from 'recharts';
import { EmptyState, ScoreBar, InsightRow } from './primitives';
import { DialogSkeleton } from './skeletons';
import {
  scoreColor, labelColor, phaseColor, readinessColor, heatColor, earlyColor, toneColor,
  fmtScore, fmtPct, fmtPrice, readinessPctOrDash,
  localizeCopy,
} from './helpers';

// ═══════════════════════════════════════════════════════════════════════════

const TickerDetailBody = ({
  tickerLoading, tickerDetail, detailLatest, detailBriefing,
  detailLabel, detailPhase, detailInsight,
  detailReasons, detailWatchouts,
  detailSnapshotItems, detailComponentItems, detailWyckoffSignals,
  lang, t, T,
}) => {
            if (tickerLoading) return <DialogSkeleton />;
            if (!tickerDetail) return <EmptyState Icon={Search} title={T('dialog.noData')} />;
            return (
            <>
              {/* Technical brief */}
              <Box sx={{ mb: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <Box sx={{ px: 1.5, py: 1.1, borderBottom: '1px solid #e2e8f0', bgcolor: alpha(toneColor(detailInsight?.tone, '#3b82f6'), 0.03) }}>
                  <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1.2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.65 }}>
                        <Chip
                          size="small"
                          label={detailLabel}
                          sx={{
                            height: 20,
                            bgcolor: alpha(labelColor(detailLatest?.accum_break_label), 0.1),
                            color: labelColor(detailLatest?.accum_break_label),
                            fontWeight: 800,
                            fontSize: '0.58rem',
                            border: `1px solid ${alpha(labelColor(detailLatest?.accum_break_label), 0.2)}`,
                          }}
                        />
                        {tickerDetail.wyckoff?.phase && (
                          <Chip
                            size="small"
                            label={detailPhase}
                            sx={{
                              height: 20,
                              bgcolor: alpha(phaseColor(tickerDetail.wyckoff?.phase), 0.1),
                              color: phaseColor(tickerDetail.wyckoff?.phase),
                              fontWeight: 800,
                              fontSize: '0.58rem',
                              border: `1px solid ${alpha(phaseColor(tickerDetail.wyckoff?.phase), 0.2)}`,
                            }}
                          />
                        )}
                        {detailLatest?.entry_heat_label && (
                          <Chip
                            size="small"
                            label={localizeCopy(detailLatest.entry_heat_label, lang)}
                            sx={{
                              height: 20,
                              fontSize: '0.58rem',
                              fontWeight: 800,
                              bgcolor: alpha(heatColor(detailLatest?.entry_heat_level, detailLatest?.entry_heat_score), 0.1),
                              color: heatColor(detailLatest?.entry_heat_level, detailLatest?.entry_heat_score),
                              border: `1px solid ${alpha(heatColor(detailLatest?.entry_heat_level, detailLatest?.entry_heat_score), 0.18)}`,
                            }}
                          />
                        )}
                      </Box>
                      <Typography sx={{ fontSize: '0.98rem', fontWeight: 850, color: toneColor(detailInsight?.tone, '#0f172a'), lineHeight: 1.25 }}>
                        {localizeCopy(detailInsight?.title, lang) || T('dialog.investorTakeaways')}
                      </Typography>
                      <Typography sx={{ mt: 0.28, fontSize: '0.7rem', color: '#64748b', lineHeight: 1.45, maxWidth: 320 }}>
                        {localizeCopy(detailInsight?.body, lang) || ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.8, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '1.55rem', fontWeight: 900, color: '#0f172a', lineHeight: 1, fontFamily: 'monospace' }}>
                        {fmtPrice(detailLatest?.close)}
                      </Typography>
                      <Chip
                        size="small"
                        label={localizeCopy(detailInsight?.action_label, lang) || T('dialog.nextStep')}
                        sx={{
                          height: 22,
                          fontSize: '0.6rem',
                          fontWeight: 800,
                          bgcolor: alpha(toneColor(detailInsight?.tone, '#3b82f6'), 0.1),
                          color: toneColor(detailInsight?.tone, '#3b82f6'),
                          border: `1px solid ${alpha(toneColor(detailInsight?.tone, '#3b82f6'), 0.2)}`,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: '1px', bgcolor: '#e2e8f0' }}>
                  {[
                    { label: T('table.price'), value: fmtPrice(detailLatest?.close), color: '#0f172a' },
                    { label: T('dialog.triggerPrice'), value: fmtPrice(detailLatest?.entry_break_level), color: '#10b981' },
                    { label: T('dialog.stopLoss'), value: fmtPrice(detailLatest?.stop_loss_level), color: '#ef4444' },
                    { label: T('dialog.markupReadiness'), value: readinessPctOrDash(tickerDetail?.wyckoff?.readiness), color: readinessColor(tickerDetail?.wyckoff?.readiness) },
                    { label: T('table.distPivot'), value: fmtPct(detailLatest?.dist_to_pivot), color: '#f59e0b' },
                    { label: T('dialog.entryHeat'), value: localizeCopy(detailLatest?.entry_heat_label, lang) || '—', color: heatColor(detailLatest?.entry_heat_level, detailLatest?.entry_heat_score) },
                    { label: T('dialog.adjustedScore'), value: fmtScore(detailLatest?.adjusted_score), color: scoreColor(detailLatest?.adjusted_score) },
                    { label: T('dialog.earlyScore'), value: localizeCopy(detailBriefing?.early_signal_label, lang) || localizeCopy(detailLatest?.metric_snapshot_view?.early_signal_score?.text, lang) || '—', color: earlyColor(detailLatest?.early_signal_score ?? detailLatest?.EarlySignalScore) },
                  ].map(({ label, value, color }) => (
                    <Box key={label} sx={{ bgcolor: '#fff', px: 1.2, py: 0.9 }}>
                      <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
                      <Typography sx={{ fontSize: '0.86rem', fontWeight: 800, color, lineHeight: 1.3, mt: 0.18 }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {detailSnapshotItems.length > 0 && (
                <Box sx={{ mb: 2, p: 1.4, bgcolor: '#fff', borderRadius: 1.8, border: '1px solid #e2e8f0' }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155', mb: 1 }}>
                    <Assessment sx={{ fontSize: 15, mr: 0.5, verticalAlign: 'text-bottom', color: '#3b82f6' }} />
                    {T('dialog.quickRead')}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1px', bgcolor: '#e2e8f0', borderRadius: 1.4, overflow: 'hidden' }}>
                    {detailSnapshotItems.map(({ label, text, detail, color, value }) => (
                      <Box key={label} sx={{ bgcolor: '#fff', px: 1.2, py: 0.95 }}>
                        <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {label}
                        </Typography>
                        <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color, lineHeight: 1.35, mt: 0.18 }}>
                          {localizeCopy(text, lang) || value || '—'}
                        </Typography>
                        {detail ? (
                          <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', mt: 0.15, lineHeight: 1.4 }}>
                            {localizeCopy(detail, lang)}
                          </Typography>
                        ) : null}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Vertical briefing flow */}
              <Stack spacing={1.2} sx={{ mb: 2 }}>
                {detailReasons.length > 0 && (
                  <Box sx={{ p: 1.35, bgcolor: '#fff', borderRadius: 1.8, border: '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155', mb: 1 }}>
                      <Star sx={{ fontSize: 15, mr: 0.5, verticalAlign: 'text-bottom', color: '#f59e0b' }} />
                      {T('dialog.investorTakeaways')}
                    </Typography>
                    <Stack spacing={0}>
                      {detailReasons.map((reason, idx) => (
                        <InsightRow key={reason.key} item={reason} tone="positive" lang={lang} isLast={idx === detailReasons.length - 1} />
                      ))}
                    </Stack>
                  </Box>
                )}

                <Box sx={{ p: 1.35, bgcolor: '#fff', borderRadius: 1.8, border: '1px solid #e2e8f0' }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155', mb: 1 }}>
                    <Warning sx={{ fontSize: 15, mr: 0.5, verticalAlign: 'text-bottom', color: '#f59e0b' }} />
                    {T('dialog.watchouts')}
                  </Typography>
                  {detailWatchouts.length > 0 ? (
                    <Stack spacing={0}>
                      {detailWatchouts.map((risk, idx) => (
                        <InsightRow key={risk.key} item={risk} tone="watchout" lang={lang} isLast={idx === detailWatchouts.length - 1} />
                      ))}
                    </Stack>
                  ) : (
                    <Typography sx={{ fontSize: '0.68rem', color: '#64748b', lineHeight: 1.45, py: 0.35 }}>
                      {T('dialog.watchoutsNone')}
                    </Typography>
                  )}
                </Box>

                {(tickerDetail?.wyckoff?.score_accumulation_sm != null || tickerDetail?.wyckoff?.score_markup_sm != null) && (
                  <Box sx={{ p: 1.35, bgcolor: '#fff', borderRadius: 1.8, border: '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155', mb: 1 }}>
                      <TrendingUp sx={{ fontSize: 15, mr: 0.5, verticalAlign: 'text-bottom', color: '#8b5cf6' }} />
                      {T('dialog.structureBias')}
                    </Typography>
                    <Stack spacing={1}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.35 }}>
                          <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8', fontWeight: 700 }}>{T('dialog.accumulationBias')}</Typography>
                          <Chip
                            size="small"
                            label={localizeCopy(tickerDetail?.wyckoff?.structure_bias_view?.accumulation?.strength, lang) || '—'}
                            sx={{
                              height: 20,
                              borderRadius: 999,
                              fontSize: '0.6rem',
                              fontWeight: 800,
                              bgcolor: alpha(toneColor(tickerDetail?.wyckoff?.structure_bias_view?.accumulation?.tone, '#94a3b8'), 0.1),
                              color: toneColor(tickerDetail?.wyckoff?.structure_bias_view?.accumulation?.tone, '#94a3b8'),
                              border: `1px solid ${alpha(toneColor(tickerDetail?.wyckoff?.structure_bias_view?.accumulation?.tone, '#94a3b8'), 0.18)}`,
                            }}
                          />
                        </Box>
                        <ScoreBar score={(tickerDetail?.wyckoff?.score_accumulation_sm || 0) * 100} />
                        <Typography sx={{ fontSize: '0.66rem', color: '#475569', lineHeight: 1.45, mt: 0.45 }}>
                          {localizeCopy(tickerDetail?.wyckoff?.structure_bias_view?.accumulation?.detail, lang) || '—'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', lineHeight: 1.45, mt: 0.12 }}>
                          {localizeCopy(tickerDetail?.wyckoff?.structure_bias_view?.accumulation?.decision, lang) || '—'}
                        </Typography>
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.35 }}>
                          <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8', fontWeight: 700 }}>{T('dialog.markupBias')}</Typography>
                          <Chip
                            size="small"
                            label={localizeCopy(tickerDetail?.wyckoff?.structure_bias_view?.markup?.strength, lang) || '—'}
                            sx={{
                              height: 20,
                              borderRadius: 999,
                              fontSize: '0.6rem',
                              fontWeight: 800,
                              bgcolor: alpha(toneColor(tickerDetail?.wyckoff?.structure_bias_view?.markup?.tone, '#94a3b8'), 0.1),
                              color: toneColor(tickerDetail?.wyckoff?.structure_bias_view?.markup?.tone, '#94a3b8'),
                              border: `1px solid ${alpha(toneColor(tickerDetail?.wyckoff?.structure_bias_view?.markup?.tone, '#94a3b8'), 0.18)}`,
                            }}
                          />
                        </Box>
                        <ScoreBar score={(tickerDetail?.wyckoff?.score_markup_sm || 0) * 100} />
                        <Typography sx={{ fontSize: '0.66rem', color: '#475569', lineHeight: 1.45, mt: 0.45 }}>
                          {localizeCopy(tickerDetail?.wyckoff?.structure_bias_view?.markup?.detail, lang) || '—'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', lineHeight: 1.45, mt: 0.12 }}>
                          {localizeCopy(tickerDetail?.wyckoff?.structure_bias_view?.markup?.decision, lang) || '—'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                )}

                {detailComponentItems.length > 0 && (
                  <Box sx={{ p: 1.35, bgcolor: '#fff', borderRadius: 1.8, border: '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155', mb: 1 }}>
                      <Bolt sx={{ fontSize: 15, mr: 0.5, verticalAlign: 'text-bottom', color: '#f59e0b' }} />
                      {T('dialog.componentBreakdown')}
                    </Typography>
                    <Stack spacing={0.65}>
                      {detailComponentItems.map(({ key, label, text, tone }) => (
                        <Box key={key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, borderBottom: '1px dashed #eef2f7', pb: 0.5, '&:last-of-type': { borderBottom: 'none', pb: 0 } }}>
                          <Typography sx={{ fontSize: '0.67rem', color: '#475569', fontWeight: 600, lineHeight: 1.35 }}>
                            {localizeCopy(label, lang) || '—'}
                          </Typography>
                          <Box
                            sx={{
                              flexShrink: 0,
                              maxWidth: 156,
                              px: 0.85,
                              py: 0.35,
                              borderRadius: 999,
                              bgcolor: alpha(toneColor(tone, '#94a3b8'), 0.1),
                              border: `1px solid ${alpha(toneColor(tone, '#94a3b8'), 0.16)}`,
                            }}
                          >
                            <Typography sx={{ fontSize: '0.62rem', lineHeight: 1.15, fontWeight: 800, color: toneColor(tone, '#64748b'), textAlign: 'center' }}>
                              {localizeCopy(text, lang) || '—'}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>

              {/* Wyckoff Section */}
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 1.2, color: '#334155' }}>
                  <Psychology sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom', color: '#8b5cf6' }} />{T('dialog.wyckoffTitle')}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600 }}>{T('dialog.phase')}</Typography>
                    <Chip
                      label={tickerDetail.wyckoff?.phase ? t(`phase.phases.${tickerDetail.wyckoff.phase}`, tickerDetail.wyckoff.phase) : T('labels.none')}
                      size="small"
                      sx={{
                        mt: 0.3, height: 20, fontSize: '0.65rem',
                        bgcolor: alpha(phaseColor(tickerDetail.wyckoff?.phase), 0.1),
                        color: phaseColor(tickerDetail.wyckoff?.phase),
                        fontWeight: 700,
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600 }}>{T('dialog.confidence')}</Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', mt: 0.3 }}>
                      {readinessPctOrDash(tickerDetail.wyckoff?.confidence)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600 }}>{T('dialog.markupReadiness')}</Typography>
                    <Typography sx={{
                      fontSize: '0.95rem', fontWeight: 800, mt: 0.3,
                      color: readinessColor(tickerDetail.wyckoff?.readiness),
                    }}>
                      {readinessPctOrDash(tickerDetail.wyckoff?.readiness)}
                    </Typography>
                    {tickerDetail.wyckoff?.readiness_label && (
                      <Typography sx={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 500 }}>
                        {tickerDetail.wyckoff.readiness_label}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Readiness signals */}
                {tickerDetail.wyckoff?.signals && Object.keys(tickerDetail.wyckoff.signals).length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#475569', mb: 0.8 }}>
                      {T('dialog.readinessSignals')}
                    </Typography>
                    {detailWyckoffSignals.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {detailWyckoffSignals.map((item) => {
                          const tone = toneColor(item.tone, '#94a3b8');
                          return (
                            <Box key={item.key} sx={{
                              px: 0.8, py: 0.3, borderRadius: 1,
                              bgcolor: alpha(tone, 0.08),
                              border: `1px solid ${alpha(tone, 0.15)}`,
                            }}>
                              <Typography sx={{
                                fontSize: '0.6rem', fontWeight: 700,
                                color: tone,
                              }}>
                              {localizeCopy(item.label, lang) || item.key}: {item.value}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8', lineHeight: 1.45 }}>
                        {T('dialog.readinessSignalsEmpty')}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              {/* Latest scores */}
              {tickerDetail.latest && (
                <Box sx={{ mb: 2, p: 1.35, bgcolor: '#fff', borderRadius: 1.8, border: '1px solid #e2e8f0' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 1, color: '#334155' }}>
                    <Equalizer sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom', color: '#3b82f6' }} />{T('dialog.latestScores')}
                  </Typography>
                  <Stack spacing={0}>
                    {[
                      { key: 'accumulation_score' },
                      { key: 'break_readiness_score' },
                      { key: 'total_accum_break_score' },
                      { key: 'score' },
                    ].map(({ key }) => {
                      const item = detailLatest?.latest_scores_view?.[key];
                      return (
                        <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.9, py: 0.95, borderBottom: key === 'score' ? 'none' : '1px dashed #e2e8f0' }}>
                          <Box sx={{ width: 3, borderRadius: 999, alignSelf: 'stretch', bgcolor: toneColor(item?.tone, '#94a3b8'), minHeight: 42, opacity: 0.9 }} />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              {T(`scores.${key}`)}
                            </Typography>
                            <Typography sx={{ fontSize: '0.78rem', color: toneColor(item?.tone, '#475569'), fontWeight: 800, lineHeight: 1.35, mt: 0.18 }}>
                              {localizeCopy(item?.text, lang) || '—'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.64rem', color: '#64748b', lineHeight: 1.4, mt: 0.16 }}>
                              {localizeCopy(item?.detail, lang) || '—'}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {/* History chart */}
              {tickerDetail.history && tickerDetail.history.length > 0 && (
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 1, color: '#334155' }}>
                    <ShowChart sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom', color: '#10b981' }} />{T('dialog.scoreHistory', { count: tickerDetail.history_count })}
                  </Typography>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={tickerDetail.history}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 8 }}
                        tickFormatter={(d) => d ? d.slice(5) : ''}
                      />
                      <YAxis tick={{ fontSize: 8 }} domain={[0, 100]} width={28} />
                      <RTooltip />
                      <Bar dataKey="accumulation_score" fill="#3b82f6" name={T('scores.accumulation_score')} opacity={0.7} />
                      <Bar dataKey="break_readiness_score" fill="#f59e0b" name={T('scores.break_readiness_score')} opacity={0.7} />
                      <Bar dataKey="total_accum_break_score" fill="#8b5cf6" name={T('scores.total_accum_break_score')} opacity={0.9} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </>
            );
};
TickerDetailBody.propTypes = {
  tickerLoading: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  tickerDetail: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  detailLatest: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  detailBriefing: PropTypes.object,
  detailLabel: PropTypes.string,
  detailPhase: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  detailInsight: PropTypes.object,
  detailReasons: PropTypes.array,
  detailWatchouts: PropTypes.array,
  detailSnapshotItems: PropTypes.array,
  detailComponentItems: PropTypes.array,
  detailWyckoffSignals: PropTypes.array,
  lang: PropTypes.string,
  t: PropTypes.func.isRequired,
  T: PropTypes.func.isRequired,
};

// ═══════════════════════════════════════════════════════════════════════════
// LeaderboardBlock (extracted for cognitive complexity)
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════

const TickerDetailDrawerBlock = ({ selectedTicker, setSelectedTicker, drawerCaptureRef, drawerScrollRef, exportingImage, handleExportImage, tickerLoading, tickerDetail, detailLatest, detailBriefing, detailCompanyName, detailLabel, detailPhase, detailInsight, detailReasons, detailWatchouts, detailSnapshotItems, detailComponentItems, detailWyckoffSignals, lang, t, T }) => (
      <Drawer
        anchor="right"
        open={!!selectedTicker}
        onClose={() => setSelectedTicker(null)}
        slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 }, p: 0 } } }}
      >
        <Box ref={drawerCaptureRef} sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: '#fff' }}>
        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2.5, py: 1.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#ffffff',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: 1,
              overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              display: 'flex', flexDirection: 'column', flexShrink: 0,
            }}>
              <Box sx={{ flex: 1, bgcolor: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontWeight: 900, color: '#fff', fontSize: '0.8rem', lineHeight: 1 }}>
                  {selectedTicker}
                </Typography>
              </Box>
              <Box sx={{ height: 10, bgcolor: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '0.45rem', letterSpacing: '0.12em', lineHeight: 1 }}>
                  SA
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '1rem', fontWeight: 850, color: '#1e293b', lineHeight: 1.15 }} title={detailCompanyName || selectedTicker}>
                {detailCompanyName || selectedTicker}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 500 }}>
                {T('dialog.title')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }} data-html2canvas-ignore="true" data-export-header-toolbar="true">
            <Tooltip title={exportingImage ? t('common.exportingImage', 'Đang xuất ảnh...') : t('common.exportImage', 'Xuất ảnh')} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={handleExportImage}
                  disabled={exportingImage || tickerLoading || !tickerDetail}
                  sx={{
                    width: 30,
                    height: 30,
                    color: '#475569',
                    border: '1px solid #e2e8f0',
                    bgcolor: '#fff',
                    '&:hover': { bgcolor: '#f8fafc' },
                    '&.Mui-disabled': { color: '#cbd5e1', borderColor: '#e2e8f0' },
                  }}
                >
                  {exportingImage ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlined sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={() => setSelectedTicker(null)}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box ref={drawerScrollRef} data-export-scroll-root="true" sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
          <TickerDetailBody
            tickerLoading={tickerLoading}
            tickerDetail={tickerDetail}
            detailLatest={detailLatest}
            detailBriefing={detailBriefing}
            detailLabel={detailLabel}
            detailPhase={detailPhase}
            detailInsight={detailInsight}
            detailReasons={detailReasons}
            detailWatchouts={detailWatchouts}
            detailSnapshotItems={detailSnapshotItems}
            detailComponentItems={detailComponentItems}
            detailWyckoffSignals={detailWyckoffSignals}
            lang={lang}
            t={t}
            T={T}
          />
        </Box>
        </Box>
      </Drawer>
);
TickerDetailDrawerBlock.propTypes = {
  selectedTicker: PropTypes.string,
  setSelectedTicker: PropTypes.func,
  drawerCaptureRef: PropTypes.shape({ current: PropTypes.any }),
  drawerScrollRef: PropTypes.shape({ current: PropTypes.any }),
  exportingImage: PropTypes.bool,
  handleExportImage: PropTypes.func,
  tickerLoading: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  tickerDetail: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  detailLatest: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  detailBriefing: PropTypes.object,
  detailCompanyName: PropTypes.string,
  detailLabel: PropTypes.string,
  detailPhase: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  detailInsight: PropTypes.object,
  detailReasons: PropTypes.array,
  detailWatchouts: PropTypes.array,
  detailSnapshotItems: PropTypes.array,
  detailComponentItems: PropTypes.array,
  detailWyckoffSignals: PropTypes.array,
  lang: PropTypes.string,
  t: PropTypes.func,
  T: PropTypes.func,
};

// ═══════════════════════════════════════════════════════════════════════════
// TopPickRow (extracted for cognitive complexity)
// ═══════════════════════════════════════════════════════════════════════════

export default TickerDetailDrawerBlock;
